import { css } from '@emotion/react';
import { useCallback, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  KaiWhiteIcon,
  LegacyContractorsIcon,
  LegacyDashboardIcon,
  LegacyKaiIcon,
  LegacyMarketIcon,
  LegacyProductsIcon,
  LegacyPropertiesIcon,
  LegacyTemplatesIcon,
  LegacyUsersIcon,
} from '../Icons';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { LEGACY_ADMIN_LEFT_NAV_WIDTH } from './layoutConstants';

const SIDEBAR_STYLE = css`
  label: SIDEBAR_STYLE;
  background-color: var(--leftNavSidebar);
  width: ${LEGACY_ADMIN_LEFT_NAV_WIDTH}px;
  position: fixed;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 1000;
  overflow: hidden;
  transition: width 0.3s ease-in-out;
`;

const SIDEBAR_EXPANDED_STYLE = css`
  label: SIDEBAR_EXPANDED_STYLE;
  width: 180px;
`;

const LOGO_STYLE = css`
  label: LOGO_STYLE;
  position: absolute;
  display: flex;
  align-items: center;
  padding-left: 25px;
  top: 0;
  left: 0;
  width: 120px;
  height: 80px;
  cursor: pointer;
`;

const ICON_POSITIONS = {
  dashboard: 96,
  properties: 152,
  markets: 208,
  contractors: 264,
  products: 320,
  templates: 376,
  users: 432,
} as const;

const VERSION_SECTION_STYLE = css`
  label: VERSION_SECTION_STYLE;
  position: absolute;
  bottom: 16px;
  left: 0;
  width: ${LEGACY_ADMIN_LEFT_NAV_WIDTH}px;
  text-align: center;
  ${TYPOGRAPHY2.xSmall}
  color: ${COLORS2.alwaysDark.text.green};
`;

const ICON_STYLE = css`
  label: ICON_STYLE;
  width: ${LEGACY_ADMIN_LEFT_NAV_WIDTH}px;
  height: 44px;
  padding: ${LAYOUT2.spacing1}px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
`;

const SELECTED_ICON_STYLE = css`
  label: SELECTED_ICON_STYLE;
  color: ${COLORS2.alwaysDark.text.white};
`;

const TEXT_LABEL_STYLE = css`
  label: TEXT_LABEL_STYLE;
  position: absolute;
  left: ${LEGACY_ADMIN_LEFT_NAV_WIDTH}px;
  top: 50%;
  transform: translateY(-50%);
  ${TYPOGRAPHY2.small}
  transition: color 0.2s ease;
  white-space: nowrap;
`;

const SELECTED_TEXT_LABEL_STYLE = css`
  label: SELECTED_TEXT_LABEL_STYLE;
  color: ${COLORS2.alwaysDark.text.white};
`;

const ICON_CONTAINER_STYLE = css`
  label: ICON_CONTAINER_STYLE;
  position: absolute;
  left: 0;
  width: 100%;
  height: 44px;
  color: ${COLORS2.alwaysDark.text.green};
  transition: color 0.2s ease;

  &:hover {
    color: ${COLORS2.alwaysDark.text.white};
  }
`;

const SELECTED_ICON_CONTAINER_STYLE = css`
  label: SELECTED_ICON_CONTAINER_STYLE;
  background: ${COLORS2.alwaysDark.background.dark};
`;

export const AdminLeftNavSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const [isExpanded, setIsExpanded] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const HOVER_DELAY_MS = 1000;

  const startExpandTimer = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, HOVER_DELAY_MS);
  }, []);

  const cancelExpandTimer = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsExpanded(false);
  }, []);

  const isDashboardRoute = location.pathname === '/' || location.pathname.startsWith('/dashboard');

  return (
    <div
      css={[SIDEBAR_STYLE, isExpanded && SIDEBAR_EXPANDED_STYLE]}
      onMouseEnter={startExpandTimer}
      onMouseLeave={cancelExpandTimer}
      onMouseDown={cancelExpandTimer}
    >
      <div
        css={LOGO_STYLE}
        style={{ position: 'relative' }}
        onClick={() => navigate('/')}
      >
        <LegacyKaiIcon
          style={{
            position: 'absolute',
            left: '25px',
            top: '25px',
            width: '25px',
            height: '25px',
          }}
        />
        <KaiWhiteIcon
          style={{ position: 'absolute', left: '80px', top: '25px' }}
        />
      </div>

      <Link to='/'>
        <div
          css={[
            ICON_CONTAINER_STYLE,
            isDashboardRoute && SELECTED_ICON_CONTAINER_STYLE,
            css`
              top: ${ICON_POSITIONS.dashboard}px;
            `,
          ]}
        >
          <LegacyDashboardIcon
            css={[ICON_STYLE, isDashboardRoute && SELECTED_ICON_STYLE]}
          />
          <span
            css={[
              TEXT_LABEL_STYLE,
              isDashboardRoute && SELECTED_TEXT_LABEL_STYLE,
            ]}
          >
            Dashboard
          </span>
        </div>
      </Link>

      <Link to='/properties'>
        <div
          css={[
            ICON_CONTAINER_STYLE,
            isSelected('/properties') && SELECTED_ICON_CONTAINER_STYLE,
            css`
              top: ${ICON_POSITIONS.properties}px;
            `,
          ]}
        >
          <LegacyPropertiesIcon
            css={[ICON_STYLE, isSelected('/properties') && SELECTED_ICON_STYLE]}
          />
          <span
            css={[
              TEXT_LABEL_STYLE,
              isSelected('/properties') && SELECTED_TEXT_LABEL_STYLE,
            ]}
          >
            Properties
          </span>
        </div>
      </Link>

      <Link to='/markets'>
        <div
          css={[
            ICON_CONTAINER_STYLE,
            isSelected('/markets') && SELECTED_ICON_CONTAINER_STYLE,
            css`
              top: ${ICON_POSITIONS.markets}px;
            `,
          ]}
        >
          <LegacyMarketIcon
            css={[ICON_STYLE, isSelected('/markets') && SELECTED_ICON_STYLE]}
          />
          <span
            css={[
              TEXT_LABEL_STYLE,
              isSelected('/markets') && SELECTED_TEXT_LABEL_STYLE,
            ]}
          >
            Markets
          </span>
        </div>
      </Link>

      <Link to='/contractors'>
        <div
          css={[
            ICON_CONTAINER_STYLE,
            isSelected('/contractors') && SELECTED_ICON_CONTAINER_STYLE,
            css`
              top: ${ICON_POSITIONS.contractors}px;
            `,
          ]}
        >
          <LegacyContractorsIcon
            css={[
              ICON_STYLE,
              isSelected('/contractors') && SELECTED_ICON_STYLE,
            ]}
          />
          <span
            css={[
              TEXT_LABEL_STYLE,
              isSelected('/contractors') && SELECTED_TEXT_LABEL_STYLE,
            ]}
          >
            Contractors
          </span>
        </div>
      </Link>

      <Link to='/products'>
        <div
          css={[
            ICON_CONTAINER_STYLE,
            isSelected('/products') && SELECTED_ICON_CONTAINER_STYLE,
            css`
              top: ${ICON_POSITIONS.products}px;
            `,
          ]}
        >
          <LegacyProductsIcon
            css={[ICON_STYLE, isSelected('/products') && SELECTED_ICON_STYLE]}
          />
          <span
            css={[
              TEXT_LABEL_STYLE,
              isSelected('/products') && SELECTED_TEXT_LABEL_STYLE,
            ]}
          >
            Products
          </span>
        </div>
      </Link>

      <Link to='/templates'>
        <div
          css={[
            ICON_CONTAINER_STYLE,
            isSelected('/templates') && SELECTED_ICON_CONTAINER_STYLE,
            css`
              top: ${ICON_POSITIONS.templates}px;
            `,
          ]}
        >
          <LegacyTemplatesIcon
            css={[ICON_STYLE, isSelected('/templates') && SELECTED_ICON_STYLE]}
          />
          <span
            css={[
              TEXT_LABEL_STYLE,
              isSelected('/templates') && SELECTED_TEXT_LABEL_STYLE,
            ]}
          >
            Templates
          </span>
        </div>
      </Link>

      <Link to='/users'>
        <div
          css={[
            ICON_CONTAINER_STYLE,
            isSelected('/users') && SELECTED_ICON_CONTAINER_STYLE,
            css`
              top: ${ICON_POSITIONS.users}px;
            `,
          ]}
        >
          <LegacyUsersIcon
            css={[ICON_STYLE, isSelected('/users') && SELECTED_ICON_STYLE]}
          />
          <span
            css={[
              TEXT_LABEL_STYLE,
              isSelected('/users') && SELECTED_TEXT_LABEL_STYLE,
            ]}
          >
            Users
          </span>
        </div>
      </Link>

      <div css={VERSION_SECTION_STYLE}>v{__APP_VERSION__}</div>
    </div>
  );
};
